/**
 * Track 2 — Sheet Overlay Markers, story-driven.
 *
 * Each marker on the sheet now represents an OperationalStory (from
 * `/api/sheets/:id/stories`). One story = one operational moment on
 * the drawing. Tapping a marker opens "What Happened Here" — the full
 * story drawer with related activity, people, open actions, closed
 * actions and the next step.
 *
 * Markers still cluster by zoom-aware grid so stories that share a
 * drawing area collapse at lower zoom and separate when zooming in.
 *
 * Public API (unchanged surface to SheetViewer):
 *    const { pins, renderPin, drawer, refresh } =
 *      useSheetOverlayMarkers({ sheetId, projectId, scale });
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Camera, Mic, MessageSquareWarning, ShieldAlert,
  AlertTriangle, CheckCircle2, ListChecks, Send, X, ExternalLink,
  Clock, Users, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";


// Per-kind construction-language meta. Markers paint with the highest-
// severity member's colour so the drawing shows operational priority.
const KIND = {
  photo:              { label: "Photo",            icon: Camera,               colour: "#0ea5e9", ring: "rgba(14,165,233,0.55)" },
  voice_note:         { label: "Voice note",       icon: Mic,                  colour: "#10b981", ring: "rgba(16,185,129,0.55)" },
  clarification_open: { label: "Clarification",    icon: MessageSquareWarning, colour: "#eab308", ring: "rgba(234,179,8,0.6)"  },
  field_conflict:     { label: "Field conflict",   icon: ShieldAlert,          colour: "#dc2626", ring: "rgba(220,38,38,0.6)" },
  revision_send:      { label: "Sent to foreman",  icon: Send,                 colour: "#6366f1", ring: "rgba(99,102,241,0.55)" },
  foreman_ack:        { label: "Acknowledged",     icon: CheckCircle2,         colour: "#16a34a", ring: "rgba(22,163,74,0.55)" },
  foreman_complete:   { label: "Completed",        icon: ListChecks,           colour: "#475569", ring: "rgba(71,85,105,0.55)" },
  trust_issue:        { label: "Trust issue",      icon: AlertTriangle,        colour: "#f59e0b", ring: "rgba(245,158,11,0.6)" },
};

// Order from most→least severe. Drives marker colour + drawer header tone.
const SEVERITY = [
  "field_conflict", "trust_issue", "clarification_open",
  "revision_send", "voice_note", "photo",
  "foreman_ack", "foreman_complete",
];

const STATUS_TONE = {
  conflict_open:               "#dc2626",
  waiting_for_clarification:   "#eab308",
  waiting_for_field:           "#f59e0b",
  needs_attention:             "#f97316",
  waiting_for_review:          "#6366f1",
  in_progress:                 "#0ea5e9",
  resolved:                    "#16a34a",
  closed:                      "#475569",
};


function _topKind(story) {
  const kinds = new Set((story.members || []).map((m) => m.kind));
  return SEVERITY.find((k) => kinds.has(k)) || "photo";
}


export function useSheetOverlayMarkers({ sheetId, projectId, scale = 1 }) {
  const [stories, setStories] = useState([]);
  const [active, setActive] = useState(null);

  const refresh = useCallback(async () => {
    if (!sheetId) return;
    try {
      const r = await api.get(`/sheets/${sheetId}/stories`);
      const located = (r.data?.items || []).filter(
        (s) => typeof s.nx === "number" && typeof s.ny === "number",
      );
      setStories(located);
    } catch {/* silent — overlay is supplementary */}
  }, [sheetId]);

  // Initial load + when sheet changes.
  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when field activity is broadcast (auto-rebuild hooks).
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("forgedops:field-stories-changed", h);
    return () => window.removeEventListener("forgedops:field-stories-changed", h);
  }, [refresh]);

  // Zoom-aware grid clustering — story centroids in the same cell merge.
  const pins = useMemo(() => {
    if (!stories.length) return [];
    const cell = Math.max(0.01, 0.04 / Math.max(0.5, scale));
    const buckets = new Map();
    for (const s of stories) {
      const gx = Math.floor(s.nx / cell);
      const gy = Math.floor(s.ny / cell);
      const k = `${gx}:${gy}`;
      const arr = buckets.get(k);
      if (arr) arr.push(s); else buckets.set(k, [s]);
    }
    const out = [];
    for (const arr of buckets.values()) {
      if (arr.length === 1) {
        const s = arr[0];
        out.push({
          id: `m_story_${s.id}`,
          x: s.nx, y: s.ny,
          _kind: _topKind(s),
          _story: s,
        });
      } else {
        const cx = arr.reduce((a, i) => a + i.nx, 0) / arr.length;
        const cy = arr.reduce((a, i) => a + i.ny, 0) / arr.length;
        out.push({
          id: `cl_story_${arr.map((i) => i.id).join("_").slice(0, 60)}`,
          x: cx, y: cy,
          _kind: "_cluster",
          _stories: arr,
        });
      }
    }
    return out;
  }, [stories, scale]);

  const renderPin = useCallback((p) => {
    if (p._kind === "_cluster") {
      // Highest-severity member kind across all stories in the cluster.
      const allKinds = new Set();
      for (const s of p._stories) {
        for (const m of (s.members || [])) allKinds.add(m.kind);
      }
      const top = SEVERITY.find((k) => allKinds.has(k)) || "photo";
      const meta = KIND[top] || KIND.photo;
      return (
        <button
          type="button"
          data-testid={`sheet-marker-cluster-${p.id}`}
          data-pin-id={p.id}
          onClick={(e) => { e.stopPropagation(); setActive({ kind: "_cluster", stories: p._stories }); }}
          className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center bg-transparent border-0 p-0"
          style={{
            width: 44, height: 44,
            pointerEvents: "auto",
            touchAction: "manipulation",
          }}
          aria-label={`${p._stories.length} stories here`}
        >
          <span
            className="flex items-center justify-center rounded-full border-2 shadow"
            style={{
              width: 30, height: 30,
              background: "rgba(255,255,255,0.94)",
              borderColor: meta.ring,
              color: meta.colour,
            }}
          >
            <span className="font-mono text-[10px] font-bold leading-none">{p._stories.length}</span>
          </span>
        </button>
      );
    }
    const meta = KIND[p._kind] || KIND.photo;
    const Icon = meta.icon;
    return (
      <button
        type="button"
        data-testid={`sheet-marker-${p._kind}-${p._story.id}`}
        data-viewer-overlay="1"
        data-overlay-type={`pin-marker-${p._kind}`}
        data-overlay-id={p._story.id}
        data-overlay-anchor-nx={p.nx}
        data-overlay-anchor-ny={p.ny}
        data-pin-id={p.id}
        onClick={(e) => { e.stopPropagation(); setActive({ kind: p._kind, stories: [p._story] }); }}
        className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center bg-transparent border-0 p-0"
        style={{
          width: 44, height: 44,
          pointerEvents: "auto",
          touchAction: "manipulation",
        }}
        aria-label={meta.label}
      >
        <span
          className="flex items-center justify-center rounded-full border-2 shadow"
          style={{
            width: 24, height: 24,
            background: "rgba(255,255,255,0.94)",
            borderColor: meta.ring,
            color: meta.colour,
          }}
        >
          <Icon size={12} />
        </span>
      </button>
    );
  }, []);

  const drawer = (
    <WhatHappenedHereDrawer
      active={active}
      projectId={projectId}
      onClose={() => setActive(null)}
      onChanged={refresh}
    />
  );

  return { pins, renderPin, drawer, refresh, storyCount: stories.length };
}


// ---------------------------------------------------------------------------
// Drawer — "What Happened Here". Single-story view + cluster tab strip.
// ---------------------------------------------------------------------------
function WhatHappenedHereDrawer({ active, projectId, onClose, onChanged }) {
  // Hooks-first ordering — never violate rules-of-hooks.
  const stories = useMemo(() => active?.stories || [], [active]);
  const storiesKey = useMemo(() => stories.map((s) => s.id).join("|"), [stories]);
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    setSelectedId(stories[0]?.id || null);
  }, [storiesKey, stories]);

  if (!active) return null;
  const isCluster = active.kind === "_cluster";
  const selected = stories.find((s) => s.id === selectedId) || stories[0];

  return (
    <div
      className="fixed inset-y-0 right-0 w-full sm:w-[440px] bg-[var(--fop-surface)] border-l border-[var(--fop-border)] z-40 flex flex-col"
      data-testid="sheet-marker-drawer"
      style={{ pointerEvents: "auto" }}
    >
      <header className="px-4 py-2 border-b border-[var(--fop-border)] flex items-center gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]" data-testid="sheet-marker-drawer-label">
          {isCluster ? `${stories.length} stories here` : "What Happened Here"}
        </div>
        <button onClick={onClose} aria-label="Close" data-testid="sheet-marker-drawer-close"
                className="ml-auto text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={14} /></button>
      </header>
      {isCluster && (
        <div className="px-3 py-2 border-b border-[var(--fop-border)] flex gap-1 overflow-x-auto" data-testid="sheet-marker-cluster-tabs">
          {stories.map((s) => {
            const meta = KIND[_topKind(s)] || KIND.photo;
            const Icon = meta.icon;
            const isActive = selectedId === s.id;
            return (
              <button key={s.id} onClick={() => setSelectedId(s.id)}
                      data-testid={`sheet-marker-cluster-pick-${s.id}`}
                      className={`inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-mono uppercase tracking-wider whitespace-nowrap border ${
                        isActive ? "border-[var(--fop-primary)]/55 bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
                                 : "border-transparent text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:border-[var(--fop-border)]"}`}>
                <Icon size={11} />{(s.status_label || "Story").toUpperCase()}
              </button>
            );
          })}
        </div>
      )}
      {selected && (
        <StoryBody story={selected} projectId={projectId} onChanged={onChanged} onClose={onClose} />
      )}
    </div>
  );
}


function StoryBody({ story, projectId, onChanged, onClose }) {
  const top = _topKind(story);
  const meta = KIND[top] || KIND.photo;
  const TopIcon = meta.icon;
  const statusColour = STATUS_TONE[story.status] || meta.colour;

  return (
    <div className="flex-1 overflow-y-auto p-4" data-testid="sheet-marker-detail-body">
      {/* Headline + status pill */}
      <div className="flex items-start gap-2 mb-3">
        <div
          className="mt-0.5 flex items-center justify-center rounded-full"
          style={{ width: 28, height: 28, background: "rgba(255,255,255,0.94)", border: `2px solid ${meta.ring}`, color: meta.colour }}
        >
          <TopIcon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[var(--fop-text)] text-base truncate" data-testid="sheet-marker-detail-title">
            {meta.label} on {story.sheet_number || "this sheet"}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.18em]"
               style={{ background: "rgba(0,0,0,0.04)", color: statusColour, border: `1px solid ${statusColour}55` }}
               data-testid="sheet-marker-story-status">
            {story.status_label || story.status || "Needs Attention"}
          </div>
        </div>
      </div>

      {/* Metadata grid */}
      <dl className="text-[12px] grid grid-cols-[110px_1fr] gap-y-1 mb-3">
        <Row k="Project"     v={story.project_id || projectId} />
        <Row k="Sheet"       v={story.sheet_number || story.sheet_id} />
        <Row k="First seen"  v={_fmt(story.started_at)} />
        <Row k="Last update" v={_fmt(story.last_activity_at)} />
        {story.nx != null && story.ny != null && (
          <Row k="Location" v={`${(story.nx * 100).toFixed(1)}% · ${(story.ny * 100).toFixed(1)}%`} />
        )}
      </dl>

      {/* People */}
      {(story.people || []).length > 0 && (
        <section className="mb-3" data-testid="sheet-marker-people">
          <SectionHeading icon={<Users size={11} />} label="People involved" />
          <div className="flex flex-wrap gap-1">
            {story.people.map((p) => (
              <span key={p} className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--fop-primary-soft)]/40 text-[var(--fop-text-2)] border border-[var(--fop-border)]">{p}</span>
            ))}
          </div>
        </section>
      )}

      {/* Related activity — the heart of "What Happened Here". */}
      <section className="mb-3" data-testid="sheet-marker-related-activity">
        <SectionHeading icon={<Clock size={11} />} label="Related Activity" />
        <ol className="space-y-1.5 border-l-2 border-[var(--fop-border)] pl-3">
          {(story.members || []).map((m, idx) => {
            const km = KIND[m.kind] || KIND.photo;
            const Icon = km.icon;
            const stage = idx === 0 ? "First"
              : idx === (story.members.length - 1) ? "Now"
              : (idx === 1 ? "Next" : "After that");
            return (
              <li key={`${m.kind}_${m.id}_${idx}`}
                  data-testid={`sheet-marker-activity-${m.kind}-${m.id}`}
                  className="grid grid-cols-[12px_1fr] gap-2 items-start">
                <span className="-ml-[19px] mt-0.5 inline-flex items-center justify-center rounded-full"
                      style={{ width: 14, height: 14, background: "var(--fop-surface)", color: km.colour, border: `2px solid ${km.ring}` }}>
                  <Icon size={8} />
                </span>
                <div className="text-[12px] text-[var(--fop-text)]">
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{stage}</span>
                    <span className="font-semibold text-[var(--fop-text)]">{km.label}</span>
                    {m.actor && <span className="text-[var(--fop-text-3)]">· {m.actor}</span>}
                  </div>
                  {m.title && <div className="text-[var(--fop-text-2)] truncate">{m.title}</div>}
                  {m.reason && <div className="text-[10px] text-[var(--fop-text-3)] italic">Linked: {m.reason}</div>}
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {/* Open actions (next steps). */}
      {(story.open_actions || []).length > 0 && (
        <section className="mb-3" data-testid="sheet-marker-open-actions">
          <SectionHeading icon={<ChevronRight size={11} />} label="Still needs attention" />
          <div className="flex flex-col gap-1.5">
            {story.open_actions.map((a, i) => (
              <ActionButton key={`open_${i}`} action={a} story={story} onChanged={onChanged} onClose={onClose} />
            ))}
          </div>
        </section>
      )}

      {/* Closed actions */}
      {(story.closed_actions || []).length > 0 && (
        <section className="mb-3" data-testid="sheet-marker-closed-actions">
          <SectionHeading icon={<CheckCircle2 size={11} />} label="Closed" />
          <ul className="text-[12px] text-[var(--fop-text-2)] space-y-0.5">
            {story.closed_actions.map((a, i) => (
              <li key={`closed_${i}`} className="flex items-center gap-1">
                <CheckCircle2 size={11} className="text-[var(--fop-success)]" />
                {a.label}{a.assignee_name ? ` · ${a.assignee_name}` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Standard nav links */}
      <div className="flex gap-2 flex-wrap pt-1 border-t border-[var(--fop-border)] mt-2" data-testid="sheet-marker-nav">
        <a href={`/sheets/${story.sheet_id}/field-timeline`}
           className="text-[12px] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] inline-flex items-center gap-1"
           data-testid="sheet-marker-open-timeline">
          <ExternalLink size={11} /> Open Field Timeline
        </a>
      </div>
    </div>
  );
}


function ActionButton({ action, story, onChanged }) {
  const [busy, setBusy] = useState(false);
  const onClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (action.kind === "send_to_foreman") {
        const path = action.source_kind === "photo"
          ? `/field-photos/${action.source_id}/send-to-foreman`
          : `/field-voice-notes/${action.source_id}/send-to-foreman`;
        await api.post(path, {});
        confirm.raw?.success?.("Sent to foreman");
      } else if (action.kind === "acknowledge_revision") {
        // Inbox owns the workflow — route there.
        window.location.assign(`/inbox?id=${action.source_id}`);
        return;
      } else if (action.kind === "resolve_clarification") {
        window.location.assign(`/inbox?id=${action.source_id}`);
        return;
      } else if (action.kind === "resolve_conflict") {
        window.location.assign(`/inbox?id=${action.source_id}`);
        return;
      } else if (action.kind === "complete_field_work") {
        window.location.assign(`/inbox?id=${action.source_id}`);
        return;
      }
      window.dispatchEvent(new CustomEvent("forgedops:field-stories-changed",
        { detail: { story_id: story.id } }));
      onChanged?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  }, [action, story, onChanged, busy]);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`sheet-marker-action-${action.kind}`}
      className="inline-flex items-center justify-between gap-2 px-3 py-2 rounded border border-[var(--fop-border)] hover:border-[var(--fop-primary)]/55 text-[12px] text-[var(--fop-text)] bg-[var(--fop-surface)] disabled:opacity-50"
    >
      <span className="truncate">{action.label}</span>
      <ChevronRight size={12} className="text-[var(--fop-text-3)]" />
    </button>
  );
}


function SectionHeading({ icon, label }) {
  return (
    <div className="flex items-center gap-1 mb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
      {icon}{label}
    </div>
  );
}


function Row({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] pt-0.5">{k}</dt>
      <dd className="text-[var(--fop-text-2)] truncate">{v}</dd>
    </>
  );
}


function _fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}


export default useSheetOverlayMarkers;
